import { Country, Subdivision } from '@omni/kit/Types';
import { KitIcon, KitInput, KitText, KitTouchable } from '@omni/kit/components';
import KitCallout from '@omni/kit/components/KitCallout';
import KitInputMask from '@omni/kit/components/KitInputMask';
import KitSelect, { SelectOption } from '@omni/kit/components/KitSelect';
import PlacesAutocomplete from '@omni/kit/components/PlacesAutocomplete';
import ProgressBar from '@omni/kit/components/ProgressBar';
import Show from '@omni/kit/components/Show';
import Stack from '@omni/kit/components/Stack';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import {
  getAllCountries,
  getAllSubdivisions,
} from '@omni/kit/services/LocationService';
import Colors from '@omni/kit/theming/Colors';
import Depth from '@omni/kit/theming/Depth';
import Spacing from '@omni/kit/theming/Spacing';
import {
  StateObject,
  extractAddressComponents,
} from '@omni/kit/utilities/extractAddressComponents';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import React, { ReactElement, useEffect, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';

import {
  DEFAULT_COUNTRY_VALUE,
  PERCENT_IN_WHOLE,
  checkPhoneValidation,
  formatPhoneNumber,
  stateIsPresentIfCountryRequiresIt,
} from './utilities';

const debug = require('debug')('tca:blocks:blocks:ExtraFields');
const DEFAULT_CALLOUT_POSITION = { x: 0, y: 0 };

export interface ExtraFieldsProps {
  addressLine1Value: string;
  addressLine2Value: string;
  appTitle?: string;
  cityValue: string;
  countryValue: string | number;
  dateOfBirthValue: string;
  hasPhoneAuthProviderEnabled: boolean;
  layout2Column?: boolean;
  missingAddressFields?: boolean;
  phoneNumberValue: string;
  postalCodeValue: string;
  progressActivityNumber: number;
  setAddressLine1Value: (address1: string) => void;
  setAddressLine2Value: (address2: string) => void;
  setCityValue: (city: string) => void;
  setCountryValue: (country: string | number) => void;
  setDateOfBirthValue: (dateOfBirth: string) => void;
  setPhoneNumberValue: (phone: string) => void;
  setPostalCodeValue: (postalCode: string) => void;
  setStateValue: (state: string | number) => void;
  stateValue: string | number;
}
const ExtraFields: React.FC<ExtraFieldsProps> = ({
  addressLine1Value,
  addressLine2Value,
  appTitle = '',
  cityValue,
  countryValue,
  dateOfBirthValue,
  hasPhoneAuthProviderEnabled,
  layout2Column = false,
  missingAddressFields = false,
  phoneNumberValue,
  postalCodeValue,
  progressActivityNumber,
  setAddressLine1Value,
  setAddressLine2Value,
  setCityValue,
  setCountryValue,
  setDateOfBirthValue,
  setPhoneNumberValue,
  setPostalCodeValue,
  setStateValue,
  stateValue,
}): ReactElement => {
  // ---------------------------------------------------------------------------
  // Local State

  const [countries, setCountries] = useState<SelectOption[]>([]);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [topCountriesCount, setTopCountriesCount] = useState(0);
  const [isValidBirthday, setIsValidBirthday] = useState(true);
  const [phoneErrorMessage, setPhoneErrorMessage] = useState('');
  const [calloutDimensions, setCalloutDimensions] = useState<{
    x: number;
    y: number;
  }>({ ...DEFAULT_CALLOUT_POSITION });
  const [showCallout, setShowCallout] = useState(false);
  const [progressBarCallout, setShowProgressBarCallout] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [streetValue, setStreetValue] = useState('');
  const [currentState, setCurrentState] = useState<StateObject | null>(null);

  const { t } = useTranslation(['blocks']);

  // ---------------------------------------------------------------------------
  // Callbacks

  const checkBirthdayValidation = (): void => {
    // check if your birthday is less than current year and greater than this date 01/01/1900
    const now = moment().format();
    const currentDate = moment(dateOfBirthValue, 'MM/DD/YYYY').format();
    const lowestDate = moment('01/01/1900', 'MM/DD/YYYY').format();

    if (currentDate < now && currentDate > lowestDate) {
      setIsValidBirthday(
        moment(dateOfBirthValue, 'MM/DD/YYYY', true).isValid()
      );
    } else {
      setIsValidBirthday(false);
    }

    // check if your birthday value is empty set valid birthday to true
    if (!dateOfBirthValue) {
      setIsValidBirthday(true);
    }
  };

  // check if the phone number is between 10 to 15 digits
  const isPhoneValid = (): void => {
    if (checkPhoneValidation(phoneNumberValue)) {
      setPhoneErrorMessage('');
    } else {
      setPhoneErrorMessage(t('blocks:extraFields.phoneError'));
    }
  };

  const dismissProgressBarCallout = (): void => {
    AsyncStorage.setItem('progress-bar-callout-hidden', 'true');
    setShowProgressBarCallout(false);
  };

  function handlePlaceSelected(place: any, placeDetails: any) {
    if (placeDetails) {
      const addressComponents = placeDetails.address_components;
      const lat = placeDetails.geometry.location.lat();
      const lng = placeDetails.geometry.location.lng();
      const description = placeDetails.formatted_address;
      const extractedAddress = extractAddressComponents(
        addressComponents,
        lat,
        lng,
        description
      );
      setAddressLine1Value(extractedAddress?.street || '');
      setCityValue(extractedAddress?.city || '');
      setCurrentState(extractedAddress?.state_obj);
      setPostalCodeValue(extractedAddress?.postal_code || '');
      setCountryValue(extractedAddress.country_obj.code || '');
    }
  }

  useEffect(() => {
    if (addressLine1Value && addressLine1Value !== '') {
      setShowAddressFields(true);
    }
  }, [addressLine1Value]);

  useEffect((): void => {
    const fetchCallouts = async (): Promise<void> => {
      if (progressActivityNumber < PERCENT_IN_WHOLE) {
        AsyncStorage.setItem('progress-bar-callout-hidden', 'false');
        setShowProgressBarCallout(true);
      } else {
        const callout = await AsyncStorage.getItem(
          'progress-bar-callout-hidden'
        );

        setShowProgressBarCallout(callout !== 'true');
      }
    };
    fetchCallouts();
  }, [progressActivityNumber]);

  const onLayout = (event: any): void => {
    const dimensions = event.nativeEvent.layout;
    setCalloutDimensions({
      x: Math.round(dimensions.x + 10),
      y: Math.round(dimensions.y + dimensions.height),
    });
  };

  useEffect((): void => {
    // retrieve a list of countries
    getAllCountries()
      .then(({ data }) => {
        const allCountries = data.countries;
        /** 90+% of users are from these countries, so they're at the top. */
        const topOfListCountries = [
          allCountries.find((c) => c.code === 'US'),
          allCountries.find((c) => c.code === 'CA'),
        ] as Country[];

        setTopCountriesCount(topOfListCountries.length);

        const orderedCountries = topOfListCountries.concat(
          allCountries.filter((c) => !topOfListCountries.includes(c))
        );

        setCountries(
          orderedCountries.map((country) => ({
            label: country.name,
            value: country.code,
          }))
        );

        // Set default country to US only if no country value exists
        const initialCountry = countryValue || DEFAULT_COUNTRY_VALUE;

        if (!countryValue) {
          setCountryValue(DEFAULT_COUNTRY_VALUE);
        }

        getAllSubdivisions(initialCountry)
          .then(({ data }) => {
            const stateData = data.subdivisions.map(
              (subdivision: Subdivision) => ({
                label: subdivision.name,
                value: subdivision.code,
              })
            );
            setStates(stateData);
            if (currentState) {
              const state = stateData.find(
                (item) =>
                  item.value === currentState.code ||
                  item.value === currentState.name ||
                  item.label === currentState.code ||
                  item.label === currentState.name
              );

              if (state) {
                setStateValue(state.value);
              }
            }
          })
          .catch((err) => {
            debug(err);
            setStates([]);
          });
      })
      .catch((err) => {
        debug(err);
        setCountries([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): void => {
    setStates([]);
    // retrieve a list of states based on what country is selected
    if (countryValue) {
      getAllSubdivisions(countryValue)
        .then(({ data }) => {
          const stateData = data.subdivisions.map(
            (subdivision: Subdivision) => ({
              label: subdivision.name,
              value: subdivision.code,
            })
          );
          setStates(stateData);
          if (currentState) {
            const state = stateData.find(
              (item) =>
                item.value === currentState.code ||
                item.value === currentState.name ||
                item.label === currentState.code ||
                item.label === currentState.name
            );

            if (state) {
              setStateValue(state.value);
            }
          }
        })
        .catch((err) => {
          debug(err);
          setStates([]);
        });
    } else {
      setStateValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryValue, currentState]);

  const handleOnAddressAutoFill = (text: string): void => {
    setAddressLine1Value(text);
    setShowAddressFields(true);
  };

  return (
    <>
      {showCallout || progressBarCallout ? (
        <View
          style={[styles.borderLine, { marginTop: Spacing.xl }]}
          testID='TopBorderline'
        />
      ) : null}
      <View testID='GeneralInfoContainer'>
        {showCallout ? (
          <KitCallout
            anchorX={calloutDimensions.x}
            anchorY={calloutDimensions.y}
            body={t('blocks:extraFields.callout', { appTitle })}
            dismiss={() => setShowCallout(false)}
            title={t('blocks:extraFields.completeProfile')}
          />
        ) : null}
        {progressBarCallout ? (
          <View
            style={styles.progressBarContainer}
            testID='ProgressBarContainer'
          >
            <View
              style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
              }}
              testID='ProgressBarWrapper'
            >
              <ProgressBar percent={progressActivityNumber} />
              <View
                style={{ marginLeft: Spacing.l }}
                testID='CompleteProfileContainer'
              >
                <KitText
                  color={Colors.N900}
                  fontSize={16}
                  semiBold
                  style={{ lineHeight: 24 }}
                  testID='CompleteYourProfile'
                >
                  {t('blocks:extraFields.completeProfile')}
                </KitText>
                <KitText gray fontSize={16} testID='ProgressActivityNumber'>
                  {progressActivityNumber}%
                </KitText>
              </View>
            </View>

            {progressActivityNumber === PERCENT_IN_WHOLE ? (
              <KitTouchable
                onPress={() => dismissProgressBarCallout()}
                style={{ borderRadius: 20, marginRight: Spacing.l }}
                ripple={false}
                underlayColor={Colors.N50}
              >
                <KitIcon name='remove' size={20} color={Colors.N300} />
              </KitTouchable>
            ) : (
              <KitTouchable
                onLayout={onLayout}
                onPress={() => setShowCallout(true)}
                ripple={false}
                style={{ borderRadius: 20, marginRight: Spacing.l }}
                underlayColor={Colors.N50}
              >
                <KitIcon name='info' size={20} color={Colors.N300} />
              </KitTouchable>
            )}
          </View>
        ) : null}
      </View>
      <View testID='ExtraFieldsContainer'>
        {/* TODO: turn these into an HOC which wraps a component with a error message popup. */}
        <Show show={!isValidBirthday}>
          <View
            style={[
              styles.errorLabel,
              layout2Column ? { left: '51%', top: 130 } : { top: 218 },
            ]}
            testID='BirthdayErrorMessageContainer'
          >
            <KitText
              fontSize={14}
              bold
              style={{ color: Colors.N0 }}
              testID='BirthdayErrorMessage'
            >
              {t('blocks:extraFields.validDate')}
            </KitText>
          </View>
        </Show>
        <Show show={Boolean(phoneErrorMessage && !hasPhoneAuthProviderEnabled)}>
          <View style={styles.errorLabel} testID='PhoneErrorMessageContainer'>
            <KitText
              fontSize={14}
              bold
              style={{ color: Colors.N0 }}
              testID='PhoneErrorMessage'
            >
              {phoneErrorMessage}
            </KitText>
          </View>
        </Show>

        <KitText
          bold
          fontSize={16}
          color={Colors.N900}
          style={{ marginTop: Spacing.xl }}
          testID='PersonalDetails'
        >
          {t('blocks:extraFields.personalDetails')}
        </KitText>

        <Stack
          spacing={Spacing.l}
          style={{ marginBottom: Spacing.xl }}
          testID='Stack'
        >
          <View
            style={{
              ...(layout2Column && { flexDirection: 'row' }),
              marginTop: Spacing.l,
            }}
            testID='PhoneBirthdayContainer'
          >
            <Show show={!hasPhoneAuthProviderEnabled}>
              <KitInput
                isValid={!phoneErrorMessage}
                keyboardType='numeric'
                label={t('blocks:extraFields.profile.phone')}
                onChangeText={setPhoneNumberValue}
                onBlur={() => {
                  isPhoneValid();
                  setPhoneNumberValue(formatPhoneNumber(phoneNumberValue));
                }}
                style={{
                  ...(layout2Column
                    ? { flex: 1, marginRight: Spacing.l }
                    : { marginBottom: Spacing.l }),
                }}
                testID='PhoneNumber'
                value={phoneNumberValue}
              />
            </Show>
            <View
              style={{ ...(layout2Column && { flex: 1 }) }}
              testID='BirthdayContainer'
            >
              <KitText semiBold style={styles.label} testID='BirthdayLabel'>
                {t('blocks:extraFields.profile.birthday')}
              </KitText>
              <KitInputMask
                isValid={isValidBirthday}
                keyboardType='numeric'
                onBlur={() => checkBirthdayValidation()}
                onChangeText={(masked) => {
                  setDateOfBirthValue(masked);
                }}
                placeholder='MM/DD/YYYY'
                value={dateOfBirthValue}
                testID='Birthday'
              />
            </View>
          </View>
          <KitText
            bold
            fontSize={16}
            color={Colors.N900}
            style={{ marginTop: 6 }}
            testID='Label Address'
          >
            {t('blocks:extraFields.profile.address')}
          </KitText>
          <KitSelect
            dividerIndex={topCountriesCount}
            isValid={!missingAddressFields || Boolean(countryValue)}
            items={countries}
            label={t('blocks:extraFields.profile.country')}
            onValueChange={(value) => setCountryValue(value.value)}
            placeholder={t('blocks:extraFields.profile.country')}
            value={countryValue}
            testID='Country'
          />
          {Platform.OS === 'web' ? (
            <>
              <KitText
                color={Colors.N500}
                fontSize={14}
                semiBold
                style={{ lineHeight: 18, marginBottom: Spacing.s }}
                testID='Label'
              >
                {t('blocks:extraFields.profile.address')}
              </KitText>
              <PlacesAutocomplete
                onPlaceSelected={handlePlaceSelected}
                onAddressAutoFill={handleOnAddressAutoFill}
                onChangeText={setAddressLine1Value}
                testID='locationInput'
                value={addressLine1Value}
                listWrapperStyle={{
                  ...styles.autocompleteListWrapper,
                  ...(showAddressFields
                    ? {
                        position: 'absolute',
                        top: '100%',
                        marginTop: Spacing.s,
                        left: 0,
                        right: 0,
                      }
                    : { marginTop: Spacing.s }),
                }}
              />
            </>
          ) : (
            <KitInput
              label={t('blocks:extraFields.profile.address')}
              onChangeText={setAddressLine1Value}
              testID='Address1'
              value={addressLine1Value}
              style={{ marginBottom: Spacing.m }}
              autoComplete='address-line1'
              key='addressLine1'
            />
          )}
          {Platform.OS !== 'web' || showAddressFields ? (
            <>
              <KitInput
                label={t('blocks:extraFields.profile.address2')}
                onChangeText={setAddressLine2Value}
                testID='Address2'
                value={addressLine2Value}
                style={{ marginBottom: Spacing.l }}
                autoComplete='address-line2'
                key='addressLine2'
              />
              <KitInput
                isValid={!missingAddressFields || Boolean(cityValue)}
                label={t('blocks:extraFields.profile.city')}
                onChangeText={setCityValue}
                testID='City'
                value={cityValue}
                style={{ marginBottom: Spacing.l }}
                autoComplete='postal-address-locality'
                key='addressLocality'
              />
              <View
                style={{ ...(layout2Column && { flexDirection: 'row' }) }}
                testID='StateAndPostalCodeContainer'
              >
                <View
                  style={{
                    ...(layout2Column
                      ? { flex: 1, marginRight: Spacing.l }
                      : { marginBottom: Spacing.l }),
                  }}
                  testID='StateContainer'
                >
                  <KitSelect
                    isValid={
                      !missingAddressFields ||
                      stateIsPresentIfCountryRequiresIt(
                        countryValue,
                        stateValue
                      )
                    }
                    items={states}
                    label={t('blocks:extraFields.profile.state')}
                    onValueChange={(value) => setStateValue(value.value)}
                    placeholder={t('blocks:extraFields.profile.state')}
                    value={stateValue}
                    testID='State'
                    key='stateInput'
                  />
                </View>
                <KitInput
                  isValid={!missingAddressFields || Boolean(postalCodeValue)}
                  label={t('blocks:extraFields.profile.zip')}
                  onChangeText={setPostalCodeValue}
                  style={{ ...(layout2Column && { flex: 1 }) }}
                  testID='PostalCode'
                  value={postalCodeValue}
                  autoComplete='postal-code'
                  key='postalCodeValue'
                />
              </View>
            </>
          ) : null}
        </Stack>
        <View
          style={[
            styles.borderLine,
            {
              marginBottom: Spacing.xl,
              marginTop: showAddressFields
                ? 0
                : Platform.OS === 'web'
                ? -24
                : Spacing.xl,
            },
          ]}
          testID='BottomBorderLine'
        />
      </View>
    </>
  );
};

export default ExtraFields;

const styles = StyleSheet.create({
  borderLine: {
    borderTopColor: Colors.N100,
    borderTopWidth: 1,
  },
  errorLabel: {
    backgroundColor: Colors.R500,
    borderRadius: Spacing.s,
    marginTop: Spacing.s,
    paddingBottom: Spacing.s,
    paddingLeft: Spacing.l,
    paddingRight: Spacing.l,
    paddingTop: Spacing.s,
    position: 'absolute',
    top: 130,
    zIndex: 2,
  },
  label: {
    color: Colors.N500,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 18,
    marginBottom: Spacing.s,
  },
  progressBarContainer: {
    alignItems: 'center',
    borderBottomColor: Colors.N100,
    borderBottomWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: Spacing.xl,
    paddingBottom: Spacing.xl,
  },
  autocompleteListWrapper: {
    backgroundColor: 'white',
    borderRadius: 16,
    paddingHorizontal: Spacing.l,
    zIndex: 10,
    ...Depth.xxl,
  },
});
